import type { SocialShareType } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { articleShareWidgetArticle$key } from "~/queries/__generated__/articleShareWidgetArticle.graphql";

import type { ContentShareWidgetProps } from "./styles";
import { Container, StyledContentShareWidget } from "./styles";

type Variants =
  | "default"
  | "morningStudio"
  | "morningStudioSubheader"
  | "newsletter"
  | "longRead"
  | "longReadMobile"
  | "photoEssayLeft"
  | "photoEssay"
  | "photoEssayMobile"
  | "landingBanner";

type OptionsMap = Record<
  Variants,
  {
    $iconBackgroundColor?: string;
    $iconColor?: string;
    direction: ContentShareWidgetProps["$direction"];
    types: SocialShareType[];
  }
>;

type Props = {
  className?: string;
  reference: articleShareWidgetArticle$key;
  variant?: Variants;
};

export const ArticleShareWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "default",
}) => {
  const content = useFragment<articleShareWidgetArticle$key>(
    graphql`
      fragment articleShareWidgetArticle on Article {
        ...contentShareWidgetContent
        ...articleCommentsTriggerArticle
      }
    `,
    reference_,
  );

  const optionsMap: OptionsMap = {
    default: {
      direction: "column",
      types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
    },
    landingBanner: {
      $iconBackgroundColor: "#ffffff",
      $iconColor: "#001246",
      direction: "row",
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    longRead: {
      direction: "row",
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    longReadMobile: {
      direction: "row",
      types: ["facebook", "twitter"],
    },
    morningStudio: {
      direction: "row",
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    morningStudioSubheader: {
      $iconBackgroundColor: "#909090",
      $iconColor: "#000000",
      direction: "row",
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    newsletter: {
      direction: "row",
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    photoEssay: {
      $iconBackgroundColor: "#ffffff",
      $iconColor: "#000000",
      direction: "row",
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    photoEssayLeft: {
      $iconBackgroundColor: "#ffffff",
      $iconColor: "#000000",
      direction: "column",
      types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
    },
    photoEssayMobile: {
      $iconBackgroundColor: "#ffffff",
      $iconColor: "#000000",
      direction: "row",
      types: ["facebook", "twitter"],
    },
  };

  const tooltipPlacement = optionsMap[variant].direction === "column" ? "right" : "top";

  return (
    <Container className={className}>
      <StyledContentShareWidget
        $direction={optionsMap[variant].direction}
        extraProps={{
          copyLink: {
            tooltipPlacement,
            variant: "tooltip",
          },
        }}
        position="article_adhesive"
        reference={content}
        style={{
          backgroundColor: optionsMap[variant].$iconBackgroundColor,
          color: optionsMap[variant].$iconColor,
          /* stylelint-disable-next-line csstree/validator */
          size: 20,
        }}
        types={optionsMap[variant].types}
      />
    </Container>
  );
};

ArticleShareWidget.displayName = "ArticleShareWidget";
